import "./styles.scss";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Loader, Plus } from "lucide-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Fieldset } from "primereact/fieldset";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import storageApi from "@/api/storage";
import ClipArtInputField from "@/components/common/ClipArtInputField";
import { ClipArt } from "@/interfaces/clip-art.interface";
import InputField from "@/components/common/InputField";
import SearchInput from "@/components/common/SearchInput";
import SelectField from "@/components/common/SelectField";
import aiFilterApi from "@/api/ai-filter";
import { uploadAIFilterJson } from "@/utils/ai-filter";
import {
  AIFilter,
  AIFilterCategory,
  AIFilterProvider,
} from "@/interfaces/ai-filter.interface";
import { InputTextarea } from "primereact/inputtextarea";
import {
  AIFilterActions,
  AIFilterConfig,
  AIFilterDetails,
  AIFilterName,
  AIFilterProviderCell,
  AIFilterTypeCell,
} from "./AIFilterDetails";
import {
  ASPECT_RATIO_AUTO_VALUE,
  AIFilterType,
  buildConfigByProvider,
  BuiltInProcessingConfigForm,
  CUTOUT_CROP_FIELD_OPTIONS,
  CUTOUT_OUTPUT_FORMAT_FIELD_OPTIONS,
  CutoutConfigForm,
  fetchGoogleImageModelFieldOptions,
  fetchOpenAIImageModelFieldOptions,
  getDefaultBuiltInProcessingConfig,
  getDefaultCutoutConfig,
  getDefaultGoogleConfig,
  getDefaultOpenAIConfig,
  getDefaultTypeByProvider,
  getDefaultVertexConfig,
  getTypeFieldOptionsByProvider,
  normalizeTypeByProvider,
  GOOGLE_ASPECT_RATIO_FIELD_OPTIONS,
  GOOGLE_IMAGE_SIZE_FIELD_OPTIONS,
  GOOGLE_MODEL_FIELD_OPTIONS,
  IMAGE_SIZE_AUTO_VALUE,
  GoogleConfigForm,
  OpenAIConfigForm,
  OPENAI_SIZE_FIELD_OPTIONS,
  OPENAI_SIZE_AUTO_VALUE,
  OPENAI_MODEL_FIELD_OPTIONS,
  PROVIDER_FIELD_OPTIONS,
  supportsGoogleImageSizeConfig,
  VertexConfigForm,
} from "./form-config";
import PostProcessingFields from "./PostProcessingFields";

interface Props {
  category?: AIFilterCategory;
  onSelect?: (item: AIFilter) => void;
}

interface FilterOption {
  key: string;
  label: string;
  value: string;
}

export default function FilterList({ category, onSelect }: Props) {
  const [itemList, setItemList] = useState<AIFilter[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentItem, setCurrentItem] = useState<AIFilter>();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createProvider, setCreateProvider] =
    useState<AIFilterProvider>("google");
  const [createType, setCreateType] = useState<AIFilterType>(
    getDefaultTypeByProvider("google"),
  );
  const [createOpenAIConfig, setCreateOpenAIConfig] =
    useState<OpenAIConfigForm>(getDefaultOpenAIConfig());
  const [createGoogleConfig, setCreateGoogleConfig] =
    useState<GoogleConfigForm>(getDefaultGoogleConfig());
  const [createCutoutConfig, setCreateCutoutConfig] =
    useState<CutoutConfigForm>(getDefaultCutoutConfig());
  const [createBuiltInProcessingConfig, setCreateBuiltInProcessingConfig] =
    useState<BuiltInProcessingConfigForm>(getDefaultBuiltInProcessingConfig());
  const [createVertexConfig, setCreateVertexConfig] =
    useState<VertexConfigForm>(getDefaultVertexConfig());
  const [googleModelFieldOptions, setGoogleModelFieldOptions] = useState(
    GOOGLE_MODEL_FIELD_OPTIONS,
  );
  const [openAIModelFieldOptions, setOpenAIModelFieldOptions] = useState(
    OPENAI_MODEL_FIELD_OPTIONS,
  );
  const [createFallbackFilterId, setCreateFallbackFilterId] =
    useState("undefined");
  const [fallbackFilterOptions, setFallbackFilterOptions] = useState<
    FilterOption[]
  >([]);
  const [fallbackFilterLoading, setFallbackFilterLoading] = useState(false);
  const [createReferenceImageUrls, setCreateReferenceImageUrls] = useState<string[]>([]);
  const [createNewRefSlotKey, setCreateNewRefSlotKey] = useState(0);

  const createTypeFieldOptions = useMemo(
    () => getTypeFieldOptionsByProvider(createProvider),
    [createProvider],
  );
  const showCreateImageSizeField = useMemo(
    () =>
      createProvider === "google" &&
      supportsGoogleImageSizeConfig(createGoogleConfig.model),
    [createProvider, createGoogleConfig.model],
  );
  const showCreateBuiltInImageSizeField = useMemo(
    () =>
      createProvider === "built_in_processing" &&
      supportsGoogleImageSizeConfig(createBuiltInProcessingConfig.model),
    [createProvider, createBuiltInProcessingConfig.model],
  );
  const showCreateVertexImageSizeField = useMemo(
    () =>
      createProvider === "vertex" &&
      supportsGoogleImageSizeConfig(createVertexConfig.model),
    [createProvider, createVertexConfig.model],
  );
  const showCreateFallbackConfiguration = useMemo(
    () =>
      createProvider === "google" ||
      createProvider === "openai" ||
      createProvider === "built_in_processing" ||
      createProvider === "vertex",
    [createProvider],
  );
  const showCreateReferenceImageField = useMemo(
    () => createProvider === "google" || createProvider === "openai" || createProvider === "vertex",
    [createProvider],
  );
  const createPostProcessingValues = {
    google: createGoogleConfig,
    openai: createOpenAIConfig,
    "cutout.pro": createCutoutConfig,
    built_in_processing: createBuiltInProcessingConfig,
    vertex: createVertexConfig,
  }[createProvider];

  const canCreate = useMemo(() => {
    const hasName = Boolean(createName.trim());
    const hasGoogleModel =
      createProvider !== "google" || Boolean(createGoogleConfig.model.trim());
    const hasOpenAIModel =
      createProvider !== "openai" || Boolean(createOpenAIConfig.model.trim());
    const hasBuiltInModel =
      createProvider !== "built_in_processing" ||
      Boolean(createBuiltInProcessingConfig.model.trim());
    const hasVertexModel =
      createProvider !== "vertex" || Boolean(createVertexConfig.model.trim());
    return (
      hasName &&
      hasGoogleModel &&
      hasOpenAIModel &&
      hasBuiltInModel &&
      hasVertexModel
    );
  }, [
    createName,
    createProvider,
    createGoogleConfig.model,
    createOpenAIConfig.model,
    createBuiltInProcessingConfig.model,
    createVertexConfig.model,
  ]);

  const onChangeCreatePostProcessing = (update: any) => {
    if (createProvider === "openai") {
      setCreateOpenAIConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    if (createProvider === "cutout.pro") {
      setCreateCutoutConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    if (createProvider === "built_in_processing") {
      setCreateBuiltInProcessingConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    if (createProvider === "vertex") {
      setCreateVertexConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    setCreateGoogleConfig((prev) => ({ ...prev, ...update }));
  };

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setPage(event.page + 1);
    setSize(event.rows);
  };

  const getItemList = useCallback(() => {
    if (category) {
      setLoading(true);
      aiFilterApi
        .getMany({
          page,
          size,
          categoryId: category._id || undefined,
          search,
          sort: "-createdAt",
        })
        .then((data) => {
          setItemList(data.data);
          const pagination = data.pagination;
          if (pagination) setTotalItems(pagination.totalItems);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setItemList([]);
      setTotalItems(0);
    }
  }, [category, search, page, size]);

  useEffect(() => {
    getItemList();
  }, [getItemList]);

  useEffect(() => {
    let cancelled = false;

    const loadModelOptions = async () => {
      const [googleOptions, openAIOptions] = await Promise.all([
        fetchGoogleImageModelFieldOptions(),
        fetchOpenAIImageModelFieldOptions(),
      ]);
      if (!cancelled) {
        setGoogleModelFieldOptions(googleOptions);
        setOpenAIModelFieldOptions(openAIOptions);
      }
    };

    loadModelOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!createOpen) return;

    let cancelled = false;
    const loadFallbackFilters = async () => {
      setFallbackFilterLoading(true);
      try {
        const response = await aiFilterApi.getMany({
          page: 1,
          size: 1000,
          sort: "name",
        });

        if (cancelled) return;

        setFallbackFilterOptions(
          response.data.map((filter) => ({
            key: filter._id,
            value: filter._id,
            label: filter.name + ` (${filter.provider})`,
          })),
        );
      } catch {
        if (cancelled) return;
        setFallbackFilterOptions([]);
      } finally {
        if (!cancelled) {
          setFallbackFilterLoading(false);
        }
      }
    };

    loadFallbackFilters();

    return () => {
      cancelled = true;
    };
  }, [createOpen]);

  const resetCreateForm = () => {
    setCreateName("");
    setCreateProvider("google");
    setCreateType(getDefaultTypeByProvider("google"));
    setCreateGoogleConfig(getDefaultGoogleConfig());
    setCreateOpenAIConfig(getDefaultOpenAIConfig());
    setCreateCutoutConfig(getDefaultCutoutConfig());
    setCreateBuiltInProcessingConfig(getDefaultBuiltInProcessingConfig());
    setCreateVertexConfig(getDefaultVertexConfig());
    setCreateFallbackFilterId("undefined");
    setCreateReferenceImageUrls([]);
    setCreateNewRefSlotKey(0);
  };

  const availableCreateFallbackFilterOptions = fallbackFilterOptions;

  const createProviderModelFieldOptions = useMemo(() => {
    if (createProvider === "openai") {
      return openAIModelFieldOptions;
    }
    return googleModelFieldOptions;
  }, [createProvider, googleModelFieldOptions, openAIModelFieldOptions]);

  useEffect(() => {
    if (createProvider !== "google") {
      return;
    }

    const allowedValues = new Set(
      googleModelFieldOptions.map((option) => option.value),
    );
    const currentModel = createGoogleConfig.model.trim();
    const nextModel = googleModelFieldOptions[0]?.value || "";

    if (currentModel && allowedValues.has(currentModel)) {
      return;
    }

    if (currentModel === nextModel) {
      return;
    }

    setCreateGoogleConfig((prev) => ({
      ...prev,
      model: nextModel,
    }));
  }, [createProvider, googleModelFieldOptions, createGoogleConfig.model]);

  useEffect(() => {
    if (createProvider !== "openai") {
      return;
    }

    const allowedValues = new Set(
      openAIModelFieldOptions.map((option) => option.value),
    );
    const currentModel = createOpenAIConfig.model.trim();
    const nextModel = openAIModelFieldOptions[0]?.value || "";

    if (currentModel && allowedValues.has(currentModel)) {
      return;
    }

    if (currentModel === nextModel) {
      return;
    }

    setCreateOpenAIConfig((prev) => ({
      ...prev,
      model: nextModel,
    }));
  }, [createProvider, openAIModelFieldOptions, createOpenAIConfig.model]);

  useEffect(() => {
    if (createProvider !== "vertex") {
      return;
    }

    const allowedValues = new Set(
      googleModelFieldOptions.map((option) => option.value),
    );
    const currentModel = createVertexConfig.model.trim();
    const nextModel = googleModelFieldOptions[0]?.value || "";

    if (currentModel && allowedValues.has(currentModel)) {
      return;
    }

    if (currentModel === nextModel) {
      return;
    }

    setCreateVertexConfig((prev) => ({
      ...prev,
      model: nextModel,
    }));
  }, [createProvider, googleModelFieldOptions, createVertexConfig.model]);

  const onOpenCreate = () => {
    resetCreateForm();
    setCreateOpen(true);
  };

  const onCloseCreate = () => {
    setCreateOpen(false);
  };

  const onCreate = async () => {
    if (!category?._id) return;
    if (!canCreate) return;

    const normalizedName = createName.trim();
    const normalizedFallbackFilterId =
      createFallbackFilterId === "undefined"
        ? undefined
        : createFallbackFilterId;
    const config = buildConfigByProvider(createProvider, {
      googleConfig: createGoogleConfig,
      openAIConfig: createOpenAIConfig,
      cutoutConfig: createCutoutConfig,
      builtInProcessingConfig: createBuiltInProcessingConfig,
      vertexConfig: createVertexConfig,
    });

    setCreateLoading(true);
    try {
      const response = await aiFilterApi.createOne({
        categoryId: category._id,
        fallbackId: normalizedFallbackFilterId,
        name: normalizedName,
        provider: createProvider,
        type: createType,
        config,
        referenceImageUrls: showCreateReferenceImageField && createReferenceImageUrls.length > 0 ? createReferenceImageUrls : null,
      });

      const createdItem = response.data;
      try {
        await uploadAIFilterJson(createdItem);
      } catch {}

      onCloseCreate();
      if (page === 1) {
        getItemList();
      } else {
        setPage(1);
      }
    } catch {}
    setCreateLoading(false);
  };

  const onCloseDetails = useCallback(() => {
    setCurrentItem(undefined);
  }, []);

  const onDelete = (item: AIFilter) => {
    aiFilterApi
      .deleteOneById(item._id)
      .then((data) => {
        const deletedItem = data.data;
        setItemList((state) => state.filter((i) => i._id !== deletedItem._id));
      })
      .catch(() => {});
  };

  const onUpdated = () => {
    getItemList();
  };

  const onChangeCreateProvider = (provider: AIFilterProvider) => {
    setCreateProvider(provider);
    setCreateType(normalizeTypeByProvider(provider, createType));
  };

  return (
    <>
      <AIFilterDetails
        data={currentItem}
        onClose={onCloseDetails}
        onUpdated={onUpdated}
      />
      <div className="flex flex-col h-full overflow-auto">
        <div className="flex items-center justify-between border-b border-gray-300 p-4 gap-2">
          <div className="flex items-center overflow-auto">
            <span className="truncate font-medium">{category?.name}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {loading && <Loader className="animate-spin" size={20} />}
            <SearchInput
              onChange={() => setLoading(true)}
              onSearch={(value) => {
                setPage(1);
                setSearch(value);
              }}
            />
            <Button
              style={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
              tooltip="New filter"
              tooltipOptions={{ position: "top" }}
              icon={<Plus size={16} />}
              disabled={!category}
              onClick={onOpenCreate}
            />
          </div>
        </div>
        <DataTable
          rowHover
          className="h-full overflow-auto data-table"
          value={itemList}
          size="small"
          scrollable
        >
          <Column
            headerClassName="w-80 max-w-80 min-w-80"
            className="w-80 max-w-80 min-w-80"
            field="name"
            header="Name"
            body={(item) => <AIFilterName data={item} />}
          ></Column>
          <Column
            headerClassName="w-40 max-w-40 min-w-40"
            className="w-40 max-w-40 min-w-40"
            field="provider"
            header="Provider"
            body={(item) => <AIFilterProviderCell data={item} />}
          ></Column>
          <Column
            headerClassName="w-40 max-w-40 min-w-40"
            className="w-40 max-w-40 min-w-40"
            field="type"
            header="Type"
            body={(item) => <AIFilterTypeCell data={item} />}
          ></Column>
          <Column
            field="config"
            header="Config"
            body={(item) => <AIFilterConfig data={item} />}
          ></Column>
          <Column
            headerClassName="w-28 max-w-28 min-w-28"
            className="w-28 max-w-28 min-w-28"
            frozen
            alignFrozen="right"
            field="actions"
            header="Actions"
            body={(item) => (
              <AIFilterActions
                data={item}
                onDelete={onDelete}
                onDetails={setCurrentItem}
              />
            )}
          ></Column>
          {onSelect && (
            <Column
              headerClassName="w-28 max-w-28 min-w-28"
              className="w-28 max-w-28 min-w-28"
              style={{ borderLeft: "1px solid #e5e7eb" }}
              frozen
              alignFrozen="right"
              body={(item) => (
                <div className="flex justify-center">
                  <Button
                    severity="info"
                    style={{
                      height: 36,
                      margin: 0,
                      padding: "6px 10px",
                      gap: 4,
                    }}
                    label="Select"
                    icon={<Check size={18} />}
                    onClick={() => onSelect(item)}
                  />
                </div>
              )}
            ></Column>
          )}
        </DataTable>
        <div className="flex justify-end w-full border-gray-300 pagination border-t-1">
          <Paginator
            first={size * (page - 1)}
            rows={size}
            totalRecords={totalItems}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <Dialog
        visible={createOpen}
        header="Create AI filter"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              outlined
              label="Cancel"
              onClick={onCloseCreate}
              disabled={createLoading}
              style={{ height: 36 }}
            />
            <Button
              label="Create"
              onClick={onCreate}
              loading={createLoading}
              disabled={createLoading || !canCreate}
              style={{ height: 36 }}
            />
          </div>
        }
        onHide={onCloseCreate}
      >
        <div className="flex w-[640px] max-w-full flex-col gap-4">
          <Fieldset className="ai-filter-fieldset" legend="General">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <InputField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9"
                  label="Name"
                  value={createName}
                  onChange={(event) => setCreateName(event.target.value)}
                />
              </div>
              <SelectField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9 px-2"
                label="Provider"
                value={createProvider}
                options={PROVIDER_FIELD_OPTIONS}
                onChange={(event) =>
                  onChangeCreateProvider(event.target.value as AIFilterProvider)
                }
              />
              <SelectField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9 px-2"
                label="Type"
                value={createType}
                options={createTypeFieldOptions}
                onChange={(event) =>
                  setCreateType(event.target.value as AIFilterType)
                }
              />
            </div>
          </Fieldset>

          {showCreateFallbackConfiguration && (
            <Fieldset
              className="ai-filter-fieldset"
              legend="Fallback Configuration (Optional)"
            >
              <div className="grid grid-cols-1 gap-3">
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="AI filters"
                  value={createFallbackFilterId}
                  options={availableCreateFallbackFilterOptions}
                  disabled={fallbackFilterLoading}
                  onChange={(event) =>
                    setCreateFallbackFilterId(event.target.value)
                  }
                />
              </div>
            </Fieldset>
          )}

          <Fieldset className="ai-filter-fieldset" legend="Configuration">
            {createProvider === "google" || createProvider === "openai" ? (
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  className="flex-col items-start w-full col-span-2 gap-1"
                  inputClassName="h-9 px-2"
                  label="Model"
                  value={
                    createProvider === "openai"
                      ? createOpenAIConfig.model
                      : createGoogleConfig.model
                  }
                  options={createProviderModelFieldOptions}
                  onChange={(event) =>
                    createProvider === "openai"
                      ? setCreateOpenAIConfig((prev) => ({
                          ...prev,
                          model: event.target.value,
                        }))
                      : setCreateGoogleConfig((prev) => ({
                          ...prev,
                          model: event.target.value,
                        }))
                  }
                />
                {createProvider === "google" ? (
                  <>
                    <SelectField
                      className="flex-col items-start w-full gap-1"
                      inputClassName="h-9 px-2"
                      label="Aspect Ratio"
                      value={
                        createGoogleConfig.aspectRatio ??
                        ASPECT_RATIO_AUTO_VALUE
                      }
                      options={GOOGLE_ASPECT_RATIO_FIELD_OPTIONS}
                      onChange={(event) =>
                        setCreateGoogleConfig((prev) => ({
                          ...prev,
                          aspectRatio:
                            event.target.value === ASPECT_RATIO_AUTO_VALUE
                              ? null
                              : event.target.value,
                        }))
                      }
                    />
                    {showCreateImageSizeField && (
                      <SelectField
                        className="flex-col items-start w-full gap-1"
                        inputClassName="h-9 px-2"
                        label="Image Size"
                        value={
                          createGoogleConfig.imageSize ?? IMAGE_SIZE_AUTO_VALUE
                        }
                        options={GOOGLE_IMAGE_SIZE_FIELD_OPTIONS}
                        onChange={(event) =>
                          setCreateGoogleConfig((prev) => ({
                            ...prev,
                            imageSize:
                              event.target.value === IMAGE_SIZE_AUTO_VALUE
                                ? null
                                : event.target.value,
                          }))
                        }
                      />
                    )}
                  </>
                ) : (
                  <SelectField
                    className="flex-col items-start w-full gap-1"
                    inputClassName="h-9 px-2"
                    label="Size"
                    value={createOpenAIConfig.size ?? OPENAI_SIZE_AUTO_VALUE}
                    options={OPENAI_SIZE_FIELD_OPTIONS}
                    onChange={(event) =>
                      setCreateOpenAIConfig((prev) => ({
                        ...prev,
                        size:
                          event.target.value === OPENAI_SIZE_AUTO_VALUE
                            ? null
                            : event.target.value,
                      }))
                    }
                  />
                )}
                <div className="flex flex-col col-span-2 gap-1">
                  <label className="text-md font-medium text-gray-700">
                    Prompt
                  </label>
                  <InputTextarea
                    className="w-full px-2 border border-gray-300 rounded"
                    rows={4}
                    value={
                      createProvider === "openai"
                        ? createOpenAIConfig.prompt
                        : createGoogleConfig.prompt
                    }
                    onChange={(event) =>
                      createProvider === "openai"
                        ? setCreateOpenAIConfig((prev) => ({
                            ...prev,
                            prompt: event.target.value,
                          }))
                        : setCreateGoogleConfig((prev) => ({
                            ...prev,
                            prompt: event.target.value,
                          }))
                    }
                  />
                </div>
              </div>
            ) : createProvider === "vertex" ? (
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  className="flex-col items-start w-full col-span-2 gap-1"
                  inputClassName="h-9 px-2"
                  label="Model"
                  value={createVertexConfig.model}
                  options={googleModelFieldOptions}
                  onChange={(event) =>
                    setCreateVertexConfig((prev) => ({
                      ...prev,
                      model: event.target.value,
                    }))
                  }
                />
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="Aspect Ratio"
                  value={
                    createVertexConfig.aspectRatio ?? ASPECT_RATIO_AUTO_VALUE
                  }
                  options={GOOGLE_ASPECT_RATIO_FIELD_OPTIONS}
                  onChange={(event) =>
                    setCreateVertexConfig((prev) => ({
                      ...prev,
                      aspectRatio:
                        event.target.value === ASPECT_RATIO_AUTO_VALUE
                          ? null
                          : event.target.value,
                    }))
                  }
                />
                {showCreateVertexImageSizeField ? (
                  <SelectField
                    className="flex-col items-start w-full gap-1"
                    inputClassName="h-9 px-2"
                    label="Image Size"
                    value={
                      createVertexConfig.imageSize ?? IMAGE_SIZE_AUTO_VALUE
                    }
                    options={GOOGLE_IMAGE_SIZE_FIELD_OPTIONS}
                    onChange={(event) =>
                      setCreateVertexConfig((prev) => ({
                        ...prev,
                        imageSize:
                          event.target.value === IMAGE_SIZE_AUTO_VALUE
                            ? null
                            : event.target.value,
                      }))
                    }
                  />
                ) : (
                  <div />
                )}
                <div className="flex flex-col col-span-2 gap-1">
                  <label className="text-md font-medium text-gray-700">
                    Prompt
                  </label>
                  <InputTextarea
                    className="w-full px-2 border border-gray-300 rounded"
                    rows={4}
                    value={createVertexConfig.prompt}
                    onChange={(event) =>
                      setCreateVertexConfig((prev) => ({
                        ...prev,
                        prompt: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            ) : createProvider === "cutout.pro" ? (
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="Crop"
                  value={createCutoutConfig.crop ? "true" : "false"}
                  options={CUTOUT_CROP_FIELD_OPTIONS}
                  onChange={(event) =>
                    setCreateCutoutConfig((prev) => ({
                      ...prev,
                      crop: event.target.value === "true",
                    }))
                  }
                />
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="Output Format"
                  value={createCutoutConfig.outputFormat}
                  options={CUTOUT_OUTPUT_FORMAT_FIELD_OPTIONS}
                  onChange={(event) =>
                    setCreateCutoutConfig((prev) => ({
                      ...prev,
                      outputFormat: event.target
                        .value as CutoutConfigForm["outputFormat"],
                    }))
                  }
                />
              </div>
            ) : createProvider === "built_in_processing" ? (
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  className="flex-col items-start w-full col-span-2 gap-1"
                  inputClassName="h-9 px-2"
                  label="Model"
                  value={createBuiltInProcessingConfig.model}
                  options={googleModelFieldOptions}
                  onChange={(event) =>
                    setCreateBuiltInProcessingConfig((prev) => ({
                      ...prev,
                      model: event.target.value,
                    }))
                  }
                />
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="Aspect Ratio"
                  value={
                    createBuiltInProcessingConfig.aspectRatio ??
                    ASPECT_RATIO_AUTO_VALUE
                  }
                  options={GOOGLE_ASPECT_RATIO_FIELD_OPTIONS}
                  onChange={(event) =>
                    setCreateBuiltInProcessingConfig((prev) => ({
                      ...prev,
                      aspectRatio:
                        event.target.value === ASPECT_RATIO_AUTO_VALUE
                          ? null
                          : event.target.value,
                    }))
                  }
                />
                {showCreateBuiltInImageSizeField ? (
                  <SelectField
                    className="flex-col items-start w-full gap-1"
                    inputClassName="h-9 px-2"
                    label="Image Size"
                    value={
                      createBuiltInProcessingConfig.imageSize ??
                      IMAGE_SIZE_AUTO_VALUE
                    }
                    options={GOOGLE_IMAGE_SIZE_FIELD_OPTIONS}
                    onChange={(event) =>
                      setCreateBuiltInProcessingConfig((prev) => ({
                        ...prev,
                        imageSize:
                          event.target.value === IMAGE_SIZE_AUTO_VALUE
                            ? null
                            : event.target.value,
                      }))
                    }
                  />
                ) : (
                  <div />
                )}
              </div>
            ) : (
              <div className="text-gray-600 text-md">
                No configuration required.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <PostProcessingFields
                postProcessingType={
                  createPostProcessingValues.postProcessingType
                }
                postProcessingConfig={
                  createPostProcessingValues.postProcessingConfig
                }
                onChange={onChangeCreatePostProcessing}
              />
            </div>
            {showCreateReferenceImageField && (
              <div className="mt-3">
                <label className="text-md font-medium text-gray-700">
                  Reference Images{" "}
                  <span className="font-normal text-gray-400">(optional, max 3)</span>
                </label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {[
                    ...createReferenceImageUrls,
                    ...(createReferenceImageUrls.length < 3 ? [""] : []),
                  ].map((url, index) => (
                    <ClipArtInputField
                      key={
                        index < createReferenceImageUrls.length
                          ? `${index}-${url}`
                          : `new-ref-slot-${createNewRefSlotKey}`
                      }
                      className="max-h-35 min-h-35 w-35 flex-col items-start"
                      label=""
                      defaultValue={url ? storageApi.getURLFromPath(url) : undefined}
                      onChange={(ca: ClipArt | undefined) => {
                        if (!ca) {
                          if (index < createReferenceImageUrls.length) {
                            setCreateReferenceImageUrls(
                              createReferenceImageUrls.filter((_, i) => i !== index),
                            );
                          }
                        } else {
                          if (index < createReferenceImageUrls.length) {
                            const updated = [...createReferenceImageUrls];
                            updated[index] = ca.path;
                            setCreateReferenceImageUrls(updated);
                          } else {
                            setCreateReferenceImageUrls([...createReferenceImageUrls, ca.path]);
                            setCreateNewRefSlotKey((k) => k + 1);
                          }
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </Fieldset>
        </div>
      </Dialog>
    </>
  );
}
